import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "../store";
import { StoreCategory } from "../../types/stores";
import { createSelector } from "reselect";

interface StoresState {
  filters: {
    category: StoreCategory | "all";
    floor: string | "all";
    sortBy: "name" | "floor" | "offers" | "newest";
    searchQuery: string;
  };
  ui: {
    isGlobalSearch: boolean;
  };
}

const initialState: StoresState = {
  filters: {
    category: "all",
    floor: "all",
    sortBy: "name",
    searchQuery: "",
  },
  ui: {
    isGlobalSearch: false,
  },
};

const storesSlice = createSlice({
  name: "stores",
  initialState,
  reducers: {
    // Filter actions
    setCategory: (state, action: PayloadAction<StoreCategory | "all">) => {
      state.filters.category = action.payload;
    },
    setFloor: (state, action: PayloadAction<string>) => {
      state.filters.floor = action.payload;
    },
    setSortBy: (
      state,
      action: PayloadAction<"name" | "floor" | "offers" | "newest">
    ) => {
      state.filters.sortBy = action.payload;
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.filters.searchQuery = action.payload;
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
    },

    // UI actions
    setGlobalSearch: (state, action: PayloadAction<boolean>) => {
      state.ui.isGlobalSearch = action.payload;
    },
  },
});

// Actions
export const {
  setCategory,
  setFloor,
  setSortBy,
  setSearchQuery,
  resetFilters,
  setGlobalSearch,
} = storesSlice.actions;

// Base selectors
const selectStoresState = (state: RootState) => state.stores;

// Memoized selectors
export const selectCategory = createSelector(
  [selectStoresState],
  (stores) => stores.filters.category
);

export const selectFloor = createSelector(
  [selectStoresState],
  (stores) => stores.filters.floor
);

export const selectSortBy = createSelector(
  [selectStoresState],
  (stores) => stores.filters.sortBy
);

export const selectSearchQuery = createSelector(
  [selectStoresState],
  (stores) => stores.filters.searchQuery
);

export const selectFilters = createSelector(
  [selectStoresState],
  (stores) => stores.filters
);

export const selectHasFiltersApplied = createSelector(
  [selectStoresState],
  (stores) =>
    stores.filters.category !== "all" ||
    stores.filters.floor !== "all" ||
    stores.filters.searchQuery !== ""
);

export const selectIsGlobalSearch = createSelector(
  [selectStoresState],
  (stores) => stores.ui.isGlobalSearch
);

export const selectStoresUI = createSelector(
  [selectStoresState],
  (stores) => stores.ui
);

export default storesSlice.reducer;
